/**
 * Hologram Media MCP tools — single-image/GIF/video -> 3D hologram pipeline.
 *
 * This surface intentionally targets the Hologram Media Pipeline in
 * `@holoscript/engine/hologram` (DepthEstimation/Quilt/MV-HEVC), not HoloMap.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import {
  MVHEVCCompiler,
  QuiltCompiler,
  type MVHEVCConfig,
  type QuiltConfig,
} from '@holoscript/engine/hologram';
import { renderHologramBundle } from './hologram-renderer';
import { callHologramWorkerRender, isHologramWorkerConfigured } from './hologram-worker-client';
import { publishHologramTeamFeed, sendHologramTeamMessage } from './hologram-holomesh-send';
import { getHologramAsset, uploadHologramBundle } from './hologram-bundle-client';
import { resolveSecretWithLease, VaultLeaseError } from './holomesh/identity/vault-lease-registry';
import { assertPublicCaptureConsent, capturePrivacyInputSchema } from './capture-consent';

/**
 * Phase 2 wrapper around `process.env.HOLOMESH_API_KEY` for hot per-request
 * hologram routes (publish_feed, send). Migration-window passthrough until
 * HOLOMESH_VAULT_LEASE_ENFORCE flips on, after which the read is gated by
 * an active task lease scoped to `env:HOLOMESH_API_KEY`. Returning '' on
 * VaultLeaseError preserves the previous `?.trim() || ''` semantics.
 */
function readHolomeshApiKey(): string {
  try {
    return resolveSecretWithLease('env:HOLOMESH_API_KEY')?.trim() ?? '';
  } catch (err) {
    if (err instanceof VaultLeaseError) return '';
    throw err;
  }
}

type HologramMediaType = 'image' | 'gif' | 'video';
type HologramTarget = 'quilt' | 'mvhevc' | 'parallax';

/**
 * Maximum inline base64 payload length accepted by the MCP tool layer before
 * we ever reach the worker or local renderer. This is intentionally tighter
 * than {@link import('../../../studio/src/app/api/hologram/_lib/store').MAX_HOLOGRAM_UPLOAD_BYTES}
 * (Studio's dedicated upload route, 100 MiB) because MCP payloads travel
 * inline over JSON-RPC and are far more expensive per byte. Override with
 * `HOLOGRAM_MCP_MAX_BASE64_BYTES` (decimal byte count).
 *
 * Source gap: .ai-ecosystem/scripts/hologram-reliability-matrix.json
 * (id: oversized-payload-rejection — abuse-path, high severity).
 */
const DEFAULT_MAX_HOLOGRAM_BASE64_BYTES = 10 * 1024 * 1024; // 10 MiB of base64 (~7.5 MiB decoded)
const HOLOGRAM_ASSETS = new Set([
  'depth.bin',
  'normal.bin',
  'quilt.png',
  'mvhevc.mp4',
  'parallax.webm',
]);

function getMaxHologramBase64Bytes(): number {
  const raw = process.env.HOLOGRAM_MCP_MAX_BASE64_BYTES;
  if (!raw) return DEFAULT_MAX_HOLOGRAM_BASE64_BYTES;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_MAX_HOLOGRAM_BASE64_BYTES;
  return parsed;
}

function assertBase64WithinLimit(b64: string, label = 'sourceBase64'): void {
  const cap = getMaxHologramBase64Bytes();
  if (b64.length > cap) {
    throw new Error(
      `hologram: oversized hologram payload — ${label} length ${b64.length} exceeds cap ${cap} bytes (set HOLOGRAM_MCP_MAX_BASE64_BYTES to override)`
    );
  }
}

/**
 * Reject obviously-invalid base64 before we build a data: URL or forward bytes
 * to the worker. This is intentionally conservative: we accept the RFC 4648
 * base64 alphabet (A–Z, a–z, 0–9, +, /) plus the URL-safe variant (-, _), and
 * tolerate trailing `=` padding plus inline whitespace. Anything else (e.g.
 * `"!!!not base64!!!"`, ASCII control chars, unicode, null) is refused with
 * an explicit "malformed media payload" error instead of silently flowing
 * into the worker as a broken data: URL.
 *
 * Source gap: .ai-ecosystem/scripts/hologram-reliability-matrix.json
 * (id: malformed-media-input — abuse-path, high severity).
 */
const BASE64_RE = /^[A-Za-z0-9+/\-_]+=*$/;

function assertBase64WellFormed(b64: string, label = 'sourceBase64'): void {
  if (b64.length === 0) {
    // Empty-string branch is handled by resolveCompositionSource which throws
    // "one of source, sourceUrl, or sourceBase64 is required"; don't shadow it.
    return;
  }
  const stripped = b64.replace(/\s+/g, '');
  if (stripped.length === 0 || stripped.length % 4 === 1 || !BASE64_RE.test(stripped)) {
    throw new Error(
      `hologram: malformed media payload — ${label} is not well-formed base64 (RFC 4648 alphabet A-Z a-z 0-9 + / = or URL-safe - _ only)`
    );
  }
}

function assertBundleBase64Field(label: string, value: string | undefined): void {
  if (!value) return;
  assertBase64WithinLimit(value, label);
  assertBase64WellFormed(value, label);
}

function assertInlineSourceWithinLimit(source: string): void {
  if (!source.startsWith('data:')) return;
  const m = /^data:[^;]+;base64,(.+)$/i.exec(source);
  if (m) {
    assertBase64WithinLimit(m[1]);
    assertBase64WellFormed(m[1]);
    return;
  }
  // `data:` prefix but no `<mime>;base64,<payload>` shape — this is a malformed
  // media payload (e.g. `data:;base64,xyz` with empty mime, or `data:image/png`
  // with no payload). Reject with an explicit error rather than silently
  // passing garbage into the composition / worker.
  // The narrow exception is `data:<mime>` with NO `;base64,` section, which
  // some existing callers pass as a composition-only source — that case is
  // kept as a pass-through in the non-base64 branch below.
  if (/^data:[^;]*;base64,?$/i.test(source)) {
    throw new Error(
      'hologram: malformed media payload — data: URL is missing a base64 payload after ";base64,"'
    );
  }
  if (/^data:;base64,/i.test(source)) {
    throw new Error(
      'hologram: malformed media payload — data: URL has empty MIME type before ";base64,"'
    );
  }
}

type HoloProperty = { key: string; value: unknown };
type HoloTrait = { name: string; config?: Record<string, unknown> };
type HoloObjectDecl = { traits?: HoloTrait[]; properties: HoloProperty[] };
type HoloComposition = { name?: string; objects: HoloObjectDecl[] };

export const hologramToolDefinitions: Tool[] = [
  {
    name: 'holo_hologram_from_media',
    description:
      'Generate a HoloScript hologram composition from a single image, GIF, or video source. Returns .holo code and trait summary.',
    inputSchema: {
      type: 'object',
      properties: {
        mediaType: {
          type: 'string',
          enum: ['image', 'gif', 'video'],
          description: 'Input media kind.',
        },
        source: {
          type: 'string',
          description:
            'Media path, file URL, or remote URL (optional if sourceUrl or sourceBase64 is set).',
        },
        sourceUrl: {
          type: 'string',
          description: 'Remote URL for media (optional alternative to source).',
        },
        sourceBase64: {
          type: 'string',
          description: 'Base64-encoded media bytes (optional alternative to source).',
        },
        name: {
          type: 'string',
          description: 'Optional object name override.',
        },
        depthScale: {
          type: 'number',
          description: 'Displacement depth scale override.',
        },
        segments: {
          type: 'number',
          description: 'Displacement subdivision segments override.',
        },
        privacy: capturePrivacyInputSchema,
      },
      required: ['mediaType'],
    },
  },
  {
    name: 'holo_hologram_compile_quilt',
    description:
      'Compile a hologram composition to Looking Glass quilt metadata and renderer code. When HOLOGRAM_WORKER_URL is set, also runs worker render for quilt and returns share URLs.',
    inputSchema: {
      type: 'object',
      properties: {
        mediaType: {
          type: 'string',
          enum: ['image', 'gif', 'video'],
        },
        source: {
          type: 'string',
          description: 'Path or URL (optional if sourceUrl or sourceBase64).',
        },
        sourceUrl: { type: 'string' },
        sourceBase64: { type: 'string' },
        name: { type: 'string' },
        skipStudioUpload: {
          type: 'boolean',
          description: 'If true, worker skips Studio upload (local/testing).',
        },
        quiltConfig: {
          type: 'object',
          description:
            'Optional QuiltConfig overrides (views, columns, rows, resolution, baseline, device, focusDistance).',
        },
        privacy: capturePrivacyInputSchema,
      },
      required: ['mediaType'],
    },
  },
  {
    name: 'holo_hologram_compile_mvhevc',
    description:
      'Compile a hologram composition to MV-HEVC (Vision Pro) stereo metadata, Swift scaffold, and mux command. When HOLOGRAM_WORKER_URL is set, also runs worker render for MV-HEVC and returns share URLs.',
    inputSchema: {
      type: 'object',
      properties: {
        mediaType: {
          type: 'string',
          enum: ['image', 'gif', 'video'],
        },
        source: {
          type: 'string',
          description: 'Path or URL (optional if sourceUrl or sourceBase64).',
        },
        sourceUrl: { type: 'string' },
        sourceBase64: { type: 'string' },
        name: { type: 'string' },
        skipStudioUpload: {
          type: 'boolean',
          description: 'If true, worker skips Studio upload (local/testing).',
        },
        mvhevcConfig: {
          type: 'object',
          description:
            'Optional MVHEVCConfig overrides (ipd, resolution, fps, convergenceDistance, fovDegrees, quality, container, disparityScale).',
        },
        privacy: capturePrivacyInputSchema,
      },
      required: ['mediaType'],
    },
  },
  {
    name: 'holo_hologram_render',
    description:
      'Render a content-addressed hologram bundle with preview/quilt/stereo artifacts. When HOLOGRAM_WORKER_URL is set and includeBase64 is false, calls the hologram worker for hash and share URLs. Set includeBase64 true to render locally for byte payloads.',
    inputSchema: {
      type: 'object',
      properties: {
        mediaType: {
          type: 'string',
          enum: ['image', 'gif', 'video'],
        },
        source: {
          type: 'string',
          description: 'Path or URL (optional if sourceUrl or sourceBase64).',
        },
        sourceUrl: { type: 'string' },
        sourceBase64: { type: 'string' },
        name: { type: 'string' },
        targets: {
          type: 'array',
          items: { type: 'string', enum: ['quilt', 'mvhevc', 'parallax'] },
          description: 'Worker render targets (ignored for local-only render).',
        },
        skipStudioUpload: {
          type: 'boolean',
          description: 'If true, worker skips Studio upload (local/testing).',
        },
        quiltConfig: {
          type: 'object',
          description:
            'Optional QuiltConfig overrides (views, columns, rows, resolution, baseline, device, focusDistance).',
        },
        mvhevcConfig: {
          type: 'object',
          description:
            'Optional MVHEVCConfig overrides (ipd, resolution, fps, convergenceDistance, fovDegrees, quality, container, disparityScale).',
        },
        includeBase64: {
          type: 'boolean',
          description:
            'Include base64 payloads for PNG artifacts. Forces local Playwright render when true.',
        },
        durationSeconds: {
          type: 'number',
          description: 'Stereo preview video duration for local render. Defaults to 2 seconds.',
        },
        privacy: capturePrivacyInputSchema,
      },
      required: ['mediaType'],
    },
  },
  {
    name: 'holo_hologram_publish_feed',
    description:
      'Publish a hologram (hash + shareUrl) to the team activity feed on HoloMesh. Uses HOLOMESH_API_KEY; poster identity is the authenticated agent. Rate-limited like holo_hologram_send.',
    inputSchema: {
      type: 'object',
      properties: {
        hash: { type: 'string', description: 'Content hash from worker or local bundle.' },
        shareUrl: {
          type: 'string',
          description: 'Public https share URL for the hologram viewer.',
        },
        teamId: {
          type: 'string',
          description: 'HoloMesh team id. Defaults to HOLOMESH_TEAM_ID when omitted.',
        },
      },
      required: ['hash', 'shareUrl'],
    },
  },
  {
    name: 'holo_hologram_send',
    description:
      'Post a hologram share link to a HoloMesh team room for a specific teammate. Validates recipient membership via HoloMesh API (HOLOMESH_API_KEY). Rate-limited per API key.',
    inputSchema: {
      type: 'object',
      properties: {
        hash: { type: 'string', description: 'Content hash from worker or local bundle.' },
        shareUrl: { type: 'string', description: 'Public share URL for the hologram asset.' },
        recipientAgentId: {
          type: 'string',
          description: 'Teammate agent id (must be on the team).',
        },
        teamId: {
          type: 'string',
          description: 'HoloMesh team id. Defaults to HOLOMESH_TEAM_ID when omitted.',
        },
        note: {
          type: 'string',
          description: 'Optional short note included in the message payload.',
        },
      },
      required: ['hash', 'shareUrl', 'recipientAgentId'],
    },
  },
  {
    name: 'holo_hologram_upload_bundle',
    description:
      'Upload a HoloGram bundle (depth + normal binaries) to the Studio store. Accepts base64-encoded depth.bin and normal.bin plus a JSON meta object. Optional quilt.png, mvhevc.mp4, and parallax.webm. Returns the content hash and canonical viewer URL.',
    inputSchema: {
      type: 'object',
      properties: {
        meta: {
          type: 'string',
          description:
            'JSON-stringified HologramMeta (schemaVersion, width, height, frames, modelId, backend, inferenceMs, createdAt, sourceKind).',
        },
        depthBinBase64: {
          type: 'string',
          description: 'Base64-encoded depth map binary (Float32Array bytes).',
        },
        normalBinBase64: {
          type: 'string',
          description: 'Base64-encoded normal map binary (Float32Array bytes, 3 channels).',
        },
        quiltPngBase64: {
          type: 'string',
          description: 'Optional base64-encoded quilt PNG.',
        },
        mvhevcMp4Base64: {
          type: 'string',
          description: 'Optional base64-encoded MV-HEVC MP4.',
        },
        parallaxWebmBase64: {
          type: 'string',
          description: 'Optional base64-encoded parallax WebM.',
        },
      },
      required: ['meta', 'depthBinBase64', 'normalBinBase64'],
    },
  },
  {
    name: 'holo_hologram_get_asset',
    description:
      'Retrieve a HoloGram bundle asset by content hash. Returns base64-encoded bytes plus MIME type. Assets: depth.bin, normal.bin, quilt.png, mvhevc.mp4, parallax.webm.',
    inputSchema: {
      type: 'object',
      properties: {
        hash: {
          type: 'string',
          description: 'Content-addressed bundle hash (64-char hex).',
        },
        asset: {
          type: 'string',
          enum: ['depth.bin', 'normal.bin', 'quilt.png', 'mvhevc.mp4', 'parallax.webm'],
          description: 'Asset name to retrieve.',
        },
      },
      required: ['hash', 'asset'],
    },
  },
  {
    name: 'holo_hologram_inject_world',
    description:
      'Inject a rendered hologram (by content hash) into a HoloLand world as a placed object on the ' +
      'semantic-state representation lane (W.701). Composes the hologram .holo code into a world ' +
      'state delta and pushes it via push_state_delta so agents and pixel-stream viewers both see it. ' +
      'Requires a HoloLand world ID and a content hash from a prior holo_hologram_render or ' +
      'holo_hologram_upload_bundle call. Returns the entity ID assigned to the hologram object ' +
      'in the world and the share URL.',
    inputSchema: {
      type: 'object',
      properties: {
        worldId: {
          type: 'string',
          description: 'HoloLand world ID to inject the hologram into.',
        },
        hash: {
          type: 'string',
          description: 'Content-addressed bundle hash (64-char hex) from a prior render/upload.',
        },
        shareUrl: {
          type: 'string',
          description: 'Public share URL for the hologram viewer (from worker or upload result).',
        },
        position: {
          type: 'array',
          items: { type: 'number' },
          minItems: 3,
          maxItems: 3,
          description:
            'World-space [x, y, z] spawn position. Defaults to [0, 1.5, -3] (eye-level, 3 m ahead).',
        },
        scale: {
          type: 'array',
          items: { type: 'number' },
          minItems: 3,
          maxItems: 3,
          description:
            'World-space [x, y, z] scale. Defaults to [2, 1.5, 1] (portrait-format hologram).',
        },
        label: {
          type: 'string',
          description: 'Optional display label for the world object. Defaults to the hash prefix.',
        },
        representationLane: {
          type: 'string',
          enum: ['semantic-state', 'pixel-stream'],
          description:
            'Which representation lane to target. Defaults to "semantic-state" (agent-native, W.701).',
        },
      },
      required: ['worldId', 'hash'],
    },
  },
];

const HOLOGRAM_NAMES = new Set(hologramToolDefinitions.map((t) => t.name));

export function isHologramToolName(name: string): boolean {
  return HOLOGRAM_NAMES.has(name);
}

function assertMediaType(value: unknown): HologramMediaType {
  const s = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (s === 'image' || s === 'gif' || s === 'video') return s;
  throw new Error('hologram: mediaType must be one of image|gif|video');
}

function resolveCompositionSource(
  args: Record<string, unknown>,
  mediaType: HologramMediaType
): string {
  const s = typeof args.source === 'string' ? args.source.trim() : '';
  const u = typeof args.sourceUrl === 'string' ? args.sourceUrl.trim() : '';
  const b64 = typeof args.sourceBase64 === 'string' ? args.sourceBase64.trim() : '';
  if (s) {
    assertInlineSourceWithinLimit(s);
    return s;
  }
  if (u) return u;
  if (b64) {
    assertBase64WithinLimit(b64);
    assertBase64WellFormed(b64);
    const mime =
      mediaType === 'image' ? 'image/png' : mediaType === 'gif' ? 'image/gif' : 'video/mp4';
    return `data:${mime};base64,${b64}`;
  }
  throw new Error('hologram: one of source, sourceUrl, or sourceBase64 is required');
}

async function buildWorkerMediaPayload(
  args: Record<string, unknown>,
  mediaType: HologramMediaType
): Promise<{ sourceUrl?: string; sourceBase64?: string }> {
  const b64Field = typeof args.sourceBase64 === 'string' ? args.sourceBase64.trim() : '';
  if (b64Field) {
    assertBase64WithinLimit(b64Field);
    assertBase64WellFormed(b64Field);
    return { sourceBase64: b64Field };
  }

  const u = typeof args.sourceUrl === 'string' ? args.sourceUrl.trim() : '';
  if (u) return { sourceUrl: u };

  const s = typeof args.source === 'string' ? args.source.trim() : '';
  if (!s) throw new Error('hologram: source media required for worker render');

  if (/^https?:\/\//i.test(s)) return { sourceUrl: s };

  if (/^data:/i.test(s)) {
    const m = /^data:[^;]+;base64,(.+)$/i.exec(s);
    if (m) {
      assertBase64WithinLimit(m[1]);
      assertBase64WellFormed(m[1]);
      return { sourceBase64: m[1] };
    }
    // Malformed data: URL (no `<mime>;base64,<payload>` section). Surface an
    // explicit error instead of falling through to the filesystem branch
    // where `readFile('data:image/png')` would produce a misleading ENOENT.
    if (/^data:[^;]*;base64,?$/i.test(s) || /^data:;base64,/i.test(s) || /^data:[^,;]*$/i.test(s)) {
      throw new Error(
        'hologram: malformed media payload — worker received a data: URL without a valid ";base64,<payload>" section'
      );
    }
  }

  const { readFile } = await import('node:fs/promises');
  const { isAbsolute, resolve } = await import('node:path');
  const fullPath = isAbsolute(s) ? s : resolve(process.cwd(), s);
  const buf = await readFile(fullPath);
  const encoded = buf.toString('base64');
  assertBase64WithinLimit(encoded);
  return { sourceBase64: encoded };
}

function parseRenderTargets(args: Record<string, unknown>): HologramTarget[] {
  const raw = args.targets;
  const fallback: HologramTarget[] = ['quilt', 'mvhevc', 'parallax'];
  if (!Array.isArray(raw) || raw.length === 0) return fallback;
  const out: HologramTarget[] = [];
  for (const t of raw) {
    const s = String(t).toLowerCase();
    if (s === 'quilt' || s === 'mvhevc' || s === 'parallax') out.push(s);
  }
  return out.length ? out : fallback;
}

function getMediaTraits(mediaType: HologramMediaType, source: string): HoloTrait[] {
  if (mediaType === 'image') {
    return [
      { name: 'image', config: { src: source } },
      { name: 'depth_estimation', config: { model: 'depth-anything-v2-small', backend: 'webgpu' } },
      { name: 'displacement', config: { scale: 0.3, segments: 128 } },
      { name: 'depth_to_normal' },
    ];
  }

  if (mediaType === 'gif') {
    return [
      { name: 'animated_texture', config: { src: source, fps: 24, max_frames: 500 } },
      { name: 'segment', config: { model: 'rembg', remove_background: true } },
      {
        name: 'depth_estimation',
        config: { model: 'depth-anything-v2-small', backend: 'webgpu', temporal_smoothing: 0.8 },
      },
      { name: 'holographic_sprite', config: { depth_scale: 0.5, render_mode: 'displacement' } },
      { name: 'billboard', config: { mode: 'camera-facing' } },
    ];
  }

  return [
    { name: 'video', config: { src: source, autoplay: true, loop: true, muted: true } },
    {
      name: 'depth_estimation',
      config: { model: 'depth-anything-v2-small', backend: 'webgpu', temporal_smoothing: 0.8 },
    },
    { name: 'displacement', config: { scale: 0.25, segments: 64 } },
  ];
}

export function buildComposition(
  mediaType: HologramMediaType,
  source: string,
  name?: string
): HoloComposition {
  const objectName = (name && name.trim()) || 'HologramMedia';
  const traits = getMediaTraits(mediaType, source);

  return {
    name: `Hologram - ${objectName}`,
    objects: [
      {
        traits,
        properties: [
          { key: 'geometry', value: 'plane' },
          { key: 'position', value: [0, 1.5, -3] },
          { key: 'scale', value: mediaType === 'video' ? [3, 1.7, 1] : [2, 1.5, 1] },
          { key: 'color', value: '#ffffff' },
        ],
      },
    ],
  };
}

export function toHoloCode(mediaType: HologramMediaType, source: string, name?: string): string {
  const objectName = (name && name.trim()) || 'HologramMedia';
  const header = `composition "Hologram - ${objectName}" {`;
  const base = [
    '  environment {',
    '    skybox: "night"',
    '    ambient_light: 0.2',
    '  }',
    '',
    `  object "${objectName}" {`,
  ];

  const body =
    mediaType === 'image'
      ? [
          `    @image src:"${source}"`,
          '    @depth_estimation { model: "depth-anything-v2-small", backend: "webgpu" }',
          '    @displacement { scale: 0.3, segments: 128 }',
          '    @depth_to_normal',
          '    geometry: "plane"',
          '    position: [0, 1.5, -3]',
          '    scale: [2, 1.5, 1]',
        ]
      : mediaType === 'gif'
        ? [
            `    @animated_texture { src: "${source}", fps: 24, max_frames: 500 }`,
            '    @segment { model: "rembg", remove_background: true }',
            '    @depth_estimation { model: "depth-anything-v2-small", backend: "webgpu", temporal_smoothing: 0.8 }',
            '    @holographic_sprite { depth_scale: 0.5, render_mode: "displacement" }',
            '    @billboard { mode: "camera-facing" }',
            '    position: [0, 1.5, -2]',
            '    scale: [2, 2, 1]',
          ]
        : [
            `    @video { src: "${source}", autoplay: true, loop: true, muted: true }`,
            '    @depth_estimation { model: "depth-anything-v2-small", backend: "webgpu", temporal_smoothing: 0.8 }',
            '    @displacement { scale: 0.25, segments: 64 }',
            '    geometry: "plane"',
            '    position: [0, 1.5, -3]',
            '    scale: [3, 1.7, 1]',
          ];

  return [header, ...base, ...body, '  }', '}'].join('\n');
}

export async function handleHologramTool(
  name: string,
  args: Record<string, unknown>
): Promise<unknown> {
  if (name === 'holo_hologram_publish_feed') {
    const hash = typeof args.hash === 'string' ? args.hash.trim() : '';
    const shareUrl = typeof args.shareUrl === 'string' ? args.shareUrl.trim() : '';
    const teamIdRaw = typeof args.teamId === 'string' ? args.teamId.trim() : '';
    const teamId = teamIdRaw || process.env.HOLOMESH_TEAM_ID?.trim() || '';
    // Phase-2 wrapped read for hologram publish_feed.
    const apiKey = readHolomeshApiKey();
    if (!hash || !shareUrl) {
      throw new Error('hologram publish_feed: hash and shareUrl are required');
    }
    if (!teamId) {
      throw new Error('hologram publish_feed: teamId is required (or set HOLOMESH_TEAM_ID)');
    }
    const out = await publishHologramTeamFeed({ teamId, apiKey, hash, shareUrl });
    return { ok: true, teamId, holomesh: out };
  }

  if (name === 'holo_hologram_send') {
    const hash = typeof args.hash === 'string' ? args.hash.trim() : '';
    const shareUrl = typeof args.shareUrl === 'string' ? args.shareUrl.trim() : '';
    const recipientAgentId =
      typeof args.recipientAgentId === 'string' ? args.recipientAgentId.trim() : '';
    const teamIdRaw = typeof args.teamId === 'string' ? args.teamId.trim() : '';
    const teamId = teamIdRaw || process.env.HOLOMESH_TEAM_ID?.trim() || '';
    const note = typeof args.note === 'string' ? args.note : undefined;
    if (!hash || !shareUrl || !recipientAgentId) {
      throw new Error('hologram send: hash, shareUrl, and recipientAgentId are required');
    }
    if (!teamId) {
      throw new Error('hologram send: teamId is required (or set HOLOMESH_TEAM_ID)');
    }
    // Phase-2 wrapped read for hologram send.
    const apiKey = readHolomeshApiKey();
    const holomesh = await sendHologramTeamMessage({
      teamId,
      apiKey,
      hash,
      shareUrl,
      recipientAgentId,
      note,
    });
    return { ok: true, teamId, recipientAgentId, holomesh };
  }

  if (name === 'holo_hologram_upload_bundle') {
    const meta = typeof args.meta === 'string' ? args.meta : '';
    const depthBinBase64 = typeof args.depthBinBase64 === 'string' ? args.depthBinBase64 : '';
    const normalBinBase64 = typeof args.normalBinBase64 === 'string' ? args.normalBinBase64 : '';
    const quiltPngBase64 =
      typeof args.quiltPngBase64 === 'string' ? args.quiltPngBase64 : undefined;
    const mvhevcMp4Base64 =
      typeof args.mvhevcMp4Base64 === 'string' ? args.mvhevcMp4Base64 : undefined;
    const parallaxWebmBase64 =
      typeof args.parallaxWebmBase64 === 'string' ? args.parallaxWebmBase64 : undefined;
    if (!meta) throw new Error('hologram upload_bundle: meta is required');
    if (!depthBinBase64) throw new Error('hologram upload_bundle: depthBinBase64 is required');
    if (!normalBinBase64) throw new Error('hologram upload_bundle: normalBinBase64 is required');
    assertBundleBase64Field('depthBinBase64', depthBinBase64);
    assertBundleBase64Field('normalBinBase64', normalBinBase64);
    assertBundleBase64Field('quiltPngBase64', quiltPngBase64);
    assertBundleBase64Field('mvhevcMp4Base64', mvhevcMp4Base64);
    assertBundleBase64Field('parallaxWebmBase64', parallaxWebmBase64);
    const result = await uploadHologramBundle({
      meta,
      depthBinBase64,
      normalBinBase64,
      quiltPngBase64,
      mvhevcMp4Base64,
      parallaxWebmBase64,
    });
    return { ok: true, ...result };
  }

  if (name === 'holo_hologram_get_asset') {
    const hash = typeof args.hash === 'string' ? args.hash.trim() : '';
    const asset = typeof args.asset === 'string' ? args.asset.trim() : '';
    if (!hash) throw new Error('hologram get_asset: hash is required');
    if (!asset) throw new Error('hologram get_asset: asset is required');
    if (!HOLOGRAM_ASSETS.has(asset)) {
      throw new Error(
        'hologram get_asset: asset must be one of depth.bin|normal.bin|quilt.png|mvhevc.mp4|parallax.webm'
      );
    }
    const result = await getHologramAsset(hash, asset);
    return { ok: true, ...result };
  }

  if (name === 'holo_hologram_inject_world') {
    return handleHologramInjectWorld(args);
  }

  const mediaType = assertMediaType(args.mediaType);
  const compositionSource = resolveCompositionSource(args, mediaType);
  assertPublicCaptureConsent(args, {
    toolName: name,
    surface: 'HoloGram',
    mediaKind: mediaType,
  });
  const objectName = typeof args.name === 'string' ? args.name : undefined;

  const composition = buildComposition(mediaType, compositionSource, objectName);
  const holoCode = toHoloCode(mediaType, compositionSource, objectName);

  switch (name) {
    case 'holo_hologram_from_media': {
      return {
        ok: true,
        mediaType,
        source: compositionSource,
        holoCode,
        traits: composition.objects[0]?.traits?.map((t) => t.name) ?? [],
      };
    }

    case 'holo_hologram_compile_quilt': {
      const compiler = new QuiltCompiler();
      const quiltConfig = (args.quiltConfig ?? undefined) as Partial<QuiltConfig> | undefined;
      const result = compiler.compileQuilt(composition, quiltConfig);
      const out: Record<string, unknown> = {
        ok: true,
        mediaType,
        source: compositionSource,
        quilt: result,
      };
      if (isHologramWorkerConfigured()) {
        try {
          const mediaPayload = await buildWorkerMediaPayload(args, mediaType);
          const wr = await callHologramWorkerRender({
            ...mediaPayload,
            mediaType,
            targets: ['quilt'],
            skipUpload: args.skipStudioUpload === true,
          });
          out.hash = wr.hash;
          out.shareUrl = wr.shareUrl;
          out.quiltUrl = wr.quiltUrl;
          out.mvhevcUrl = wr.mvhevcUrl;
        } catch (e) {
          out.workerError = e instanceof Error ? e.message : String(e);
        }
      }
      return out;
    }

    case 'holo_hologram_compile_mvhevc': {
      const compiler = new MVHEVCCompiler();
      const mvhevcConfig = (args.mvhevcConfig ?? undefined) as Partial<MVHEVCConfig> | undefined;
      const result = compiler.compileMVHEVC(composition, mvhevcConfig);
      const out: Record<string, unknown> = {
        ok: true,
        mediaType,
        source: compositionSource,
        mvhevc: result,
      };
      if (isHologramWorkerConfigured()) {
        try {
          const mediaPayload = await buildWorkerMediaPayload(args, mediaType);
          const wr = await callHologramWorkerRender({
            ...mediaPayload,
            mediaType,
            targets: ['mvhevc'],
            skipUpload: args.skipStudioUpload === true,
          });
          out.hash = wr.hash;
          out.shareUrl = wr.shareUrl;
          out.quiltUrl = wr.quiltUrl;
          out.mvhevcUrl = wr.mvhevcUrl;
        } catch (e) {
          out.workerError = e instanceof Error ? e.message : String(e);
        }
      }
      return out;
    }

    case 'holo_hologram_render': {
      const quiltCompiler = new QuiltCompiler();
      const mvhevcCompiler = new MVHEVCCompiler();
      const quiltConfig = (args.quiltConfig ?? undefined) as Partial<QuiltConfig> | undefined;
      const mvhevcConfig = (args.mvhevcConfig ?? undefined) as Partial<MVHEVCConfig> | undefined;

      const quilt = quiltCompiler.compileQuilt(composition, quiltConfig);
      const mvhevc = mvhevcCompiler.compileMVHEVC(composition, mvhevcConfig);

      const useWorker = isHologramWorkerConfigured() && args.includeBase64 !== true;
      if (useWorker) {
        const mediaPayload = await buildWorkerMediaPayload(args, mediaType);
        const workerResult = await callHologramWorkerRender({
          ...mediaPayload,
          mediaType,
          targets: parseRenderTargets(args),
          skipUpload: args.skipStudioUpload === true,
        });
        return {
          ok: true,
          mediaType,
          source: compositionSource,
          holoCode,
          quilt,
          mvhevc,
          worker: workerResult,
        };
      }

      const bundle = await renderHologramBundle({
        mediaType,
        source: compositionSource,
        name: objectName ?? 'HologramMedia',
        holoCode,
        quilt,
        mvhevc,
        includeBase64: args.includeBase64 === true,
        durationSeconds: typeof args.durationSeconds === 'number' ? args.durationSeconds : 2,
      });

      return {
        ok: true,
        mediaType,
        source: compositionSource,
        holoCode,
        bundle,
      };
    }

    default:
      return undefined;
  }
}

// ── HoloLand semantic-state lane injection ───────────────────────────────────

/**
 * Representation lane for the inject_world tool.
 * W.701: 'semantic-state' is the agent-native lane; 'pixel-stream' is the
 * WebXR/headset lane. Both read the same world state — the lane determines
 * how the state delta is serialised to the connecting party.
 */
type HologramRepresentationLane = 'semantic-state' | 'pixel-stream';

/**
 * Inject a previously-rendered hologram bundle into a HoloLand world as a
 * placed object on the semantic-state representation lane.
 *
 * Design (W.701 dual-representation model):
 *   1. Build a minimal `.holo` composition that references the hologram by
 *      its content hash (so the viewer fetches the bundle from Studio store).
 *   2. Construct a `push_state_delta` payload:
 *        - entityId   = "hologram:<hash-prefix>" — stable, collision-free ID
 *        - payload    = { type, worldId, hash, shareUrl, position, scale,
 *                         label, representationLane, holoCode, injectedAt }
 *   3. Call `handleNetworkingTool('push_state_delta', ...)` locally rather
 *      than making an HTTP round-trip — same process, no extra auth.
 *
 * Returns { ok, entityId, worldId, hash, shareUrl, representationLane }.
 */
async function handleHologramInjectWorld(args: Record<string, unknown>): Promise<unknown> {
  const worldId = typeof args.worldId === 'string' ? args.worldId.trim() : '';
  const hash = typeof args.hash === 'string' ? args.hash.trim() : '';
  const shareUrl = typeof args.shareUrl === 'string' ? args.shareUrl.trim() : '';
  const label =
    typeof args.label === 'string' && args.label.trim()
      ? args.label.trim()
      : `hologram-${hash.slice(0, 8)}`;
  const laneRaw = typeof args.representationLane === 'string' ? args.representationLane.trim() : '';
  const lane: HologramRepresentationLane =
    laneRaw === 'pixel-stream' ? 'pixel-stream' : 'semantic-state';

  if (!worldId) throw new Error('hologram inject_world: worldId is required');
  if (!hash) throw new Error('hologram inject_world: hash (content hash) is required');

  // Parse position / scale with safe defaults.
  const rawPos = Array.isArray(args.position) ? args.position : null;
  const position: [number, number, number] =
    rawPos && rawPos.length >= 3
      ? [Number(rawPos[0]) || 0, Number(rawPos[1]) || 1.5, Number(rawPos[2]) || -3]
      : [0, 1.5, -3];

  const rawScale = Array.isArray(args.scale) ? args.scale : null;
  const scale: [number, number, number] =
    rawScale && rawScale.length >= 3
      ? [Number(rawScale[0]) || 2, Number(rawScale[1]) || 1.5, Number(rawScale[2]) || 1]
      : [2, 1.5, 1];

  // Stable entity ID — deterministic from worldId + hash so the same hologram
  // injected twice into the same world produces an idempotent delta (LWW).
  const entityId = `hologram:${worldId}:${hash.slice(0, 12)}`;

  // Minimal .holo composition that references the bundle by content hash.
  // The Studio viewer resolves depth.bin / quilt.png / mvhevc.mp4 via
  // GET /api/hologram/asset/:hash/:asset.
  const holoCode = [
    `composition "HoloGram ${label}" {`,
    '  environment {',
    '    skybox: "night"',
    '    ambient_light: 0.2',
    '  }',
    `  object "${label}" {`,
    `    @hologram_bundle { hash: "${hash}"${shareUrl ? `, shareUrl: "${shareUrl}"` : ''} }`,
    `    geometry: "plane"`,
    `    position: [${position.join(', ')}]`,
    `    scale: [${scale.join(', ')}]`,
    '  }',
    '}',
  ].join('\n');

  // State delta — the payload that push_state_delta broadcasts to all
  // subscribers of this world on the semantic-state lane.
  const stateDelta = {
    type: 'hologram-object',
    worldId,
    hash,
    shareUrl: shareUrl || null,
    position,
    scale,
    label,
    representationLane: lane,
    holoCode,
    injectedAt: new Date().toISOString(),
  };

  // Dispatch locally to the networking push_state_delta handler.
  const { handleNetworkingTool } = await import('./networking-tools');
  await handleNetworkingTool('push_state_delta', { entityId, payload: stateDelta });

  return {
    ok: true,
    entityId,
    worldId,
    hash,
    shareUrl: shareUrl || null,
    representationLane: lane,
    position,
    scale,
    label,
  };
}
